import React from 'react';
import { safeLog } from '../utils/security';

interface AuthErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface AuthErrorBoundaryProps {
  children: React.ReactNode;
}

class AuthErrorBoundary extends React.Component<AuthErrorBoundaryProps, AuthErrorBoundaryState> {
  constructor(props: AuthErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AuthErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    safeLog.error('Auth Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F7F4ED]">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-theme-danger text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-theme-text mb-4">Something went wrong</h1>
            <p className="text-theme-text-muted mb-6">
              We encountered an error while processing your authentication. Please try again.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-[#0046AD] hover:bg-[#003b8f] text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              >
                Refresh Page
              </button>
              <button
                onClick={() => window.location.href = '/auth/login'}
                className="w-full bg-theme-text-muted hover:brightness-90 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
